import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useDeleteRestaurantMutation, useFetchRestaurantQuery, useFetchRestaurantsByOwnerMutation } from '@/api/hooks/useRestaurant';
import { SimpleRestaurantResponse } from '@/api/lib/restaurant';
import { useAuthContext } from '@/contexts/auth';

import { LoadingProcessAdapted } from '@/components/molecules/LoadingProcess/LoadingProcessAdapted';
import { PageTitle } from '@/components/molecules/PageTitle';
import { AddRestaurantForm } from '@/components/organisms/AddRestaurantForm';
import { ProposalRestaurantSideInfos } from '@/components/organisms/ProposalRestaurantSideInfos';

export const AddRestaurantPage = () => {
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { user } = useAuthContext();

  const { id } = useParams();
  const restaurantId = id || '';

  const { data: restaurant } = useFetchRestaurantQuery({ id: restaurantId, withMenu: false }, { enabled: !!restaurantId });

  const [_restaurantsData, setRestaurantsData] = useState<SimpleRestaurantResponse[]>([]);

  const { mutate: fetchRestaurantsByOwner } = useFetchRestaurantsByOwnerMutation({
    onSuccess: (data: SimpleRestaurantResponse[]) => {
      setRestaurantsData(data);
    }
  });

  const { isLoading: isDeletingRestaurant, mutate: deleteRestaurant } = useDeleteRestaurantMutation({
    onSuccess: () => {
      navigate('/?tab=pending');
      toast.success('Fiche de proposition supprimée', {
        position: 'bottom-right'
      });
    }
  });

  const handleDelete = () => {
    deleteRestaurant({ restaurantId });
  };

  useEffect(() => {
    if (user) {
      fetchRestaurantsByOwner({});
    }
  }, [fetchRestaurantsByOwner, user]);

  return (
    <div className="h-full w-full mx-auto">
      <div className="max-w-6xl mx-auto">
        {restaurant ? <PageTitle firstTitle="Modifier" secondTitle={restaurant.name} /> : <PageTitle firstTitle="Inscrivez votre" secondTitle="restaurant" />}
      </div>

      {restaurant && <LoadingProcessAdapted restaurant={restaurant} />}

      <div className="flex flex-col md:flex-row gap-5 md:gap-20 mt-10 pb-20 justify-center">
        {restaurant && <ProposalRestaurantSideInfos isLoading={isDeletingRestaurant} onDelete={handleDelete} restaurant={restaurant} />}
        <section className="max-w-6xl md:border rounded-xl md:p-10 p-5 ">
          <AddRestaurantForm
            restaurant={
              restaurant
                ? {
                    ...restaurant,
                    tagIds: restaurant.tags.map((tag) => tag.id)
                  }
                : undefined
            }
            initialData={{
              autocompleteName: restaurant?.name || '',
              customDeliveryLink: restaurant?.delivery?.custom,
              justEatLink: restaurant?.delivery?.directJustEat,
              emailOwner: restaurant?.emailOwner || user?.email || '',
              deliverooLink: restaurant?.delivery?.deliveroo,
              uberEatsLink: restaurant?.delivery?.uberEats,
              tagIds: restaurant?.tags.map((tag) => tag.id) || [],
              isPlaceAlreadyRegistered: false,
              autocompleteTag: restaurant?.tags.map((tag) => tag.name).join(', '),
              placeId: restaurant?.gmapPlaceId || '',
              phoneNumberOwner: restaurant?.phoneNumberOwner || '',
              firstName: restaurant?.firstNameOwner || '',
              lastName: restaurant?.lastNameOwner || '',
              alcohol: restaurant?.alcohol || false,
              company: restaurant?.digitalCertificate?.company || '',
              suppliers: restaurant?.digitalCertificate?.suppliers || []
            }}
          />
        </section>
      </div>
    </div>
  );
};
